import { useEffect, useCallback, useMemo } from 'react';

import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';

import * as productServices from 'services/product';

import { multiplePanelsAllergens } from '../product-media-area/multiple-panel/tab-allergens/queries';
import { getKeyProductIngredients } from '../product-media-area/multiple-panel/tab-ingredients/useProductIngredients';
import { CustomNotification } from 'common/components';

export const productDetailKeys = {
  modules: (productId) => ['product-details', 'modules', parseInt(productId)],
  modulesBrickCode: (brickCode) => [
    'product-details',
    'modules-brickcode',
    brickCode,
  ],
  customProperties: (productId) => [
    'product-details',
    'custom-properties',
    parseInt(productId),
  ],
  memberCustomProperties: (ownerId) => [
    'product-details',
    'member-custom-properties',
    parseInt(ownerId),
  ],
};

export const useGetProductItemModules = (productId) => {
  const queryClient = useQueryClient();

  const { data, status, ...queries } = useQuery({
    queryKey: productDetailKeys.modules(productId),
    queryFn: async () => {
      const response = await productServices.getProductItemModules({
        productId,
      });
      return response?.data;
    },
    enabled: Boolean(productId),
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const basicInformationData = useMemo(() => {
    return (
      data?.productItemModules?.find(
        (productModule) => productModule.moduleName === 'Header'
      )?.data ?? {}
    );
  }, [data]);

  const refetchDataProductModules = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: productDetailKeys.modules(productId),
    });
  }, [productId, queryClient]);

  return {
    dataProductModules: data ?? {},
    basicInformationData,
    statusFetchProductItem: status,
    refetchDataProductModules,
    ...queries,
  };
};

export const useGetProductItemModulesBrickCode = (brickCode) => {
  const { data, ...queries } = useQuery({
    queryKey: productDetailKeys.modulesBrickCode(brickCode),
    queryFn: async () => {
      const response = await productServices.getProductItemModulesByBrickCode({
        brickCode,
      });
      return response?.data;
    },
    enabled: Boolean(brickCode),
  });

  return { modulesBrickCode: data?.moduleNames ?? [], ...queries };
};

export const useGetProductCustomProperties = (productId) => {
  const queryClient = useQueryClient();

  const { data, status, ...queries } = useQuery({
    queryKey: productDetailKeys.customProperties(productId),
    queryFn: async () => {
      const response = await productServices.getProductCustomPropertiesData({
        productId,
      });
      return response?.data;
    },
    enabled: Boolean(productId),
  });

  const refetchProductCustomPropertiesData = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: productDetailKeys.customProperties(productId),
    });
  }, [productId, queryClient]);

  return {
    data,
    refetchProductCustomPropertiesData,
    ...queries,
  };
};

export const useGetMemberProductCustomProperties = (ownerId) => {
  const { data, error, ...queries } = useQuery({
    queryKey: productDetailKeys.customProperties(ownerId),
    queryFn: async () => {
      const response = await productServices.getMemberProductCustomProperties({
        memberId: ownerId,
      });
      return response?.data;
    },
    enabled: Boolean(ownerId),
  });

  return { data, error, ...queries };
};

export const useScrollToFoundModule = ({ foundModule, elementByClassName }) => {
  useEffect(() => {
    if (foundModule && elementByClassName?.[0]) {
      elementByClassName[0].scrollIntoView();
    }

    return;
  }, [elementByClassName, foundModule]);
};

export const useSyncProductModules = () => {
  const queryClient = useQueryClient();

  const handleSyncAllergens = (productId) => {
    queryClient.invalidateQueries(
      multiplePanelsAllergens.productAllergens(productId)
    );
  };

  const handleSyncIngredients = (productId) => {
    queryClient.invalidateQueries(getKeyProductIngredients(productId));
  };

  return {
    handleSyncAllergens,
    handleSyncIngredients,
  };
};

export const useSaveProductProperties = (productId) => {
  const { handleSyncAllergens, handleSyncIngredients } =
    useSyncProductModules();

  return useMutation({
    mutationFn: productServices.saveProductItemModules,
    onSuccess: (response) => {
      if (response?.isSuccess) {
        handleSyncAllergens(productId);
        handleSyncIngredients(productId);
        CustomNotification.success('Update product detail successfully');
      } else {
        CustomNotification.error(response?.message ?? 'Something went wrong');
      }
    },
    onError: (error) => {
      CustomNotification.error(error?.message ?? 'Something went wrong');
    },
  });
};
